import { useRef, useState } from 'react';
import { differenceBy, groupBy } from 'lodash';

import { getRemovedRidersRides } from '../../../api/services/Rides';
import { useTimezone } from '../../../hooks/useTimezone';
import { getDateGroup } from '../../helpers';
import { RemovedRide } from '../../types';

interface UseRemovedRidersHook {
  rides: RemovedRiderRides;
  rideCount: number;
  isFetching: boolean;
  fetchRemovedRiderRides: (params) => void;
}

interface RemovedRiderRides {
  [key: string]: RemovedRide[];
}

export const useRemovedRiders = (): UseRemovedRidersHook => {
  const [rides, setRides] = useState<RemovedRiderRides>({});
  const [rideCount, setRideCount] = useState<number>(0);

  const [isFetching, setIsFetching] = useState(false);
  const { getDateInTimezone, getEndOfDate } = useTimezone();
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchRemovedRiderRides = async (params: QueryParams) => {
    abortControllerRef.current?.abort();

    const abortController = new AbortController();

    abortControllerRef.current = abortController;

    setIsFetching(true);

    const formattedparams = {
      ...params,
      fromDate: getEndOfDate(new Date(params.fromDate as string)).toISOString(),
    };

    const response = await getRemovedRidersRides(formattedparams, abortController.signal);

    if (abortController.signal.aborted) {
      return;
    }

    if (response.type === 'success') {
      const ridesReturnedCount = response.data.trips?.length;
      const newGroupedRides = groupBy(response.data.trips, (ride) => getDateGroup(getDateInTimezone(ride.startsAt)));

      setRideCount((currentCount) => (params.page === 1 ? ridesReturnedCount : currentCount + ridesReturnedCount));
      setRides((prevRides) => (params.page === 1 ? newGroupedRides : combineRides(prevRides, newGroupedRides)));
    }
    setIsFetching(false);
  };

  return { rides, rideCount, isFetching, fetchRemovedRiderRides };
};

const combineRides = (existingRides: RemovedRiderRides, newRides: RemovedRiderRides): RemovedRiderRides => {
  const combinedRides = existingRides;

  for (const rideDay in newRides) {
    if (existingRides[rideDay]) {
      combinedRides[rideDay] = [
        ...existingRides[rideDay],
        ...differenceBy(newRides[rideDay], existingRides[rideDay], 'slug'),
      ];
    } else {
      combinedRides[rideDay] = newRides[rideDay];
    }
  }

  return combinedRides;
};
